import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCalendarDays,
    faCalendarCheck,
} from '@fortawesome/free-solid-svg-icons';
import {
    EngagementClosingDate as EngagementClosingDateModel,
    ClosingDateProposalStatus,
} from '../../../slices/marketplace/closing-date/models/engagement-closing-date.model';
import { getPartyName } from '../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { formatLongBusinessDatePt } from '../../../slices/marketplace/timeline/utils/timeline-dates';

const EMPTY_DATE_LABEL = 'Not yet available';

const formatBusinessDate = (instantUtc?: string | null): string =>
    instantUtc ? formatLongBusinessDatePt(instantUtc) : EMPTY_DATE_LABEL;

export interface ClosingDateCardProps {
    closingDate: EngagementClosingDateModel;
}

export const ClosingDateCard = ({ closingDate }: ClosingDateCardProps) => {
    const {
        currentProposal,
        defaultClosingDate,
        officialClosingDate,
        isEngagementClosed,
    } = closingDate;
    const isAccepted =
        currentProposal?.status === ClosingDateProposalStatus.Accepted;

    const statusLabel = isEngagementClosed
        ? 'Transaction Closing Date'
        : !currentProposal
        ? 'Default Closing Date'
        : isAccepted
        ? 'Current Closing Date'
        : 'Proposed Closing Date';

    const primaryDate = isEngagementClosed
        ? officialClosingDate
        : !currentProposal
        ? defaultClosingDate
        : isAccepted
        ? officialClosingDate ?? currentProposal.proposedDate
        : currentProposal.proposedDate;

    const isPrimaryDateAttributable =
        !!currentProposal && (isAccepted || !isEngagementClosed);

    const showsAgreedDateAside =
        !!officialClosingDate && primaryDate !== officialClosingDate;
    const asideLabel = showsAgreedDateAside
        ? 'Current Closing Date'
        : 'Default Closing Date';
    const asideDate = showsAgreedDateAside
        ? officialClosingDate
        : defaultClosingDate;

    return (
        <div className="closing-date-card">
            <div className="closing-date-card__icon">
                <FontAwesomeIcon
                    icon={isAccepted ? faCalendarCheck : faCalendarDays}
                />
            </div>
            <div className="closing-date-card__primary">
                <div className="closing-date-card__label">{statusLabel}</div>
                <div className="closing-date-card__date">
                    {formatBusinessDate(primaryDate)}
                </div>
                {isPrimaryDateAttributable && (
                    <div className="closing-date-card__proposed-by">
                        {isAccepted ? 'Set from proposal by ' : 'Proposed by '}
                        <strong>{currentProposal.proposedByName}</strong>
                        <span className="closing-date-card__party">
                            {getPartyName(currentProposal.proposedByParty)}
                        </span>
                        <span className="closing-date-card__separator">
                            ·
                        </span>
                        <span>
                            {formatBusinessDate(currentProposal.proposedAt)}
                        </span>
                    </div>
                )}
            </div>
            {currentProposal && (
                <div className="closing-date-card__default">
                    <div className="closing-date-card__default-item">
                        <div
                            className={
                                showsAgreedDateAside
                                    ? 'closing-date-card__default-label closing-date-card__default-label--agreed'
                                    : 'closing-date-card__default-label'
                            }>
                            {asideLabel}
                        </div>
                        <div className="closing-date-card__default-date">
                            {formatBusinessDate(asideDate)}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
